import React, {
  useContext,
  useState,
  useRef,
  useEffect,
  Fragment,
} from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  Navigate,
  useParams,
  useNavigate,
} from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { FcPrevious, FcNext } from "react-icons/fc";
import { IoChevronDown, IoChevronUp, IoSearch } from "react-icons/io5";
import { HiOutlineViewList } from "react-icons/hi";
import axios from "axios";
import {
  Menu,
  Transition,
  MenuButton,
  MenuItems,
  MenuItem,
} from "@headlessui/react";

function Topbar({ isDarkMode, setIsDarkMode, showDarkModeToggle = false }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { logout, userId, firstName } = useContext(AuthContext);
  const [questions, setQuestions] = useState([]);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch questions data
  useEffect(() => {
    const fetchQuestionsCount = async () => {
      if (!userId) return;

      try {
        console.log("Fetching questions count...");
        const response = await axios.get(
          "https://sqlguroo.com/subapi/questions-count"
        );

        console.log("Questions count API response:", response.data);

        if (response.data && response.data.success) {
          setTotalQuestions(response.data.totalQuestions);
          console.log("Total questions set to:", response.data.totalQuestions);
        } else {
          console.error("Invalid questions count data format:", response.data);
          setTotalQuestions(100); // Default fallback
        }
      } catch (error) {
        console.error("Error fetching questions count:", error);
        setTotalQuestions(100); // Default fallback
      }
    };

    // Always fetch the count for navigation
    fetchQuestionsCount();
  }, [userId, id]);

  // Fetch full questions data when dropdown is opened
  const fetchQuestionsForDropdown = async () => {
    if (!userId || questions.length > 0) return; // Don't fetch if already loaded

    setLoading(true);
    try {
      console.log("Fetching questions for dropdown with userId:", userId);
      const response = await axios.get(
        "https://sqlguroo.com/subapi/questions-status",
        { params: { userId } }
      );

      console.log("Questions status API response:", response.data);

      if (response.data && Array.isArray(response.data)) {
        setQuestions(response.data);

        // Find current question
        const current = response.data.find((q) => q.id === parseInt(id));
        setCurrentQuestion(current);
        console.log("Current question:", current);
      } else {
        console.error("Invalid questions data format:", response.data);
      }
    } catch (error) {
      console.error("Error fetching questions for dropdown:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDropdownToggle = () => {
    if (!isDropdownOpen && questions.length === 0) {
      // Fetch questions when opening dropdown for the first time
      fetchQuestionsForDropdown();
    }
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handlePrevPage = () => {
    const prevPage = parseInt(id, 10) - 1;
    console.log("Going to previous page:", prevPage);
    if (prevPage < 1) return;
    navigate(`/subscribersmain/${prevPage}`);
  };

  const handleNextPage = () => {
    const nextPage = parseInt(id, 10) + 1;
    console.log(
      "Going to next page:",
      nextPage,
      "Total questions:",
      totalQuestions
    );
    // Allow navigation even if we don't have exact count, but limit to reasonable number
    const maxQuestions = totalQuestions > 0 ? totalQuestions : 1000;
    if (nextPage > maxQuestions) return;
    navigate(`/subscribersmain/${nextPage}`);
  };

  const handleJumpToQuestion = (questionId) => {
    console.log("Jumping to question:", questionId);
    navigate(`/subscribersmain/${questionId}`);
    setIsDropdownOpen(false);
    setSearchTerm("");
  };

  const handleLogout = async () => {
    localStorage.clear();
    await logout();
    navigate("/");
  };

  // Filter questions based on search term
  const filteredQuestions = questions.filter(
    (question) =>
      question.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      question.id.toString().includes(searchTerm)
  );

  const currentQuestionIndex = questions.findIndex(
    (q) => q.id === parseInt(id)
  );
  const isFirstQuestion = parseInt(id) <= 1;
  const isLastQuestion =
    totalQuestions > 0 ? parseInt(id) >= totalQuestions : false;

  return (
    <nav
      className={`${
        isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
      } border-b px-4 py-2.5 shadow-sm relative z-50`}
    >
      <div className="flex justify-between items-center w-full">
        {/* Left side: Logo and Navigation */}
        <div className="flex items-center gap-4">
          <Link to="/" className="flex-shrink-0">
            <div className="flex items-center text-5xl font-extrabold">
              <span
                className={`${isDarkMode ? "text-gray-100" : "text-gray-800"}`}
              >
                SQL
              </span>
              <div
                className={`relative ${
                  isDarkMode
                    ? "bg-blue-900 text-blue-300 border border-blue-700"
                    : "bg-blue-100 text-blue-800"
                } text-xl font-semibold me-2 px-2.5 py-0.5 rounded ms-2 flex items-center shadow-sm`}
              >
                Guroo
                {firstName && (
                  <span
                    className={`ml-2 ${
                      isDarkMode ? "text-blue-400" : "text-blue-500"
                    } font-normal`}
                  >
                    | {firstName}
                  </span>
                )}
                <div className="absolute -top-3 -right-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-8 h-8"
                    viewBox="0 0 24 24"
                    style={{
                      transform: "rotate(25deg)",
                      color: isDarkMode ? "#60a5fa" : "#1a1a1a",
                    }}
                  >
                    <path
                      fill="currentColor"
                      d="M12 2L1 7l11 5 11-5-11-5zm0 6.5l-8-3.64V15l8 3.64 8-3.64V4.86l-8 3.64z"
                    />
                    <path
                      d="M21 7L21.5 8L27.5 24"
                      stroke="currentColor"
                      strokeWidth="1.2"
                      fill="none"
                      strokeLinecap="round"
                    />
                    <circle cx="21" cy="7" r="0.5" fill="currentColor" />
                  </svg>
                </div>
              </div>
            </div>
          </Link>

          {/* Enhanced Navigation Section */}
          <div className="hidden sm:flex items-center gap-3 ml-4">
            {/* Question Progress Indicator */}
            <div
              className={`flex items-center gap-2 ${
                isDarkMode
                  ? "bg-gradient-to-r from-gray-700 to-gray-800 border-gray-600"
                  : "bg-gradient-to-r from-slate-50 to-slate-100 border-slate-200"
              } px-3 py-1.5 rounded-lg border`}
            >
              <span
                className={`text-xs font-medium ${
                  isDarkMode ? "text-gray-300" : "text-slate-600"
                }`}
              >
                Question
              </span>
              <span
                className={`text-sm font-bold ${
                  isDarkMode ? "text-gray-100" : "text-slate-800"
                }`}
              >
                {id}
              </span>
              <span
                className={`text-xs ${
                  isDarkMode ? "text-gray-400" : "text-slate-500"
                }`}
              >
                of
              </span>
              <span
                className={`text-sm font-bold ${
                  isDarkMode ? "text-gray-100" : "text-slate-800"
                }`}
              >
                {totalQuestions > 0 ? totalQuestions : "..."}
              </span>
            </div>

            {/* Navigation Controls */}
            <div className="flex items-center gap-1">
              {/* Previous Button */}
              <button
                onClick={handlePrevPage}
                disabled={isFirstQuestion}
                className={`group flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all duration-300 border ${
                  isFirstQuestion
                    ? isDarkMode
                      ? "bg-gray-700 text-gray-500 border-gray-600 cursor-not-allowed"
                      : "bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed"
                    : isDarkMode
                    ? "bg-gradient-to-r from-blue-800 to-blue-900 hover:from-blue-700 hover:to-blue-800 text-blue-300 border-blue-700 hover:border-blue-600 shadow-sm hover:shadow-md"
                    : "bg-gradient-to-r from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 text-blue-700 border-blue-200 hover:border-blue-300 shadow-sm hover:shadow-md"
                }`}
                aria-label="Previous question"
              >
                <FcPrevious
                  className={`text-lg transition-transform ${
                    !isFirstQuestion && "group-hover:-translate-x-0.5"
                  }`}
                />
                <span className="text-xs font-medium">Prev</span>
              </button>

              {/* Jump to Question Dropdown */}
              <div className="relative">
                <button
                  onClick={handleDropdownToggle}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all duration-300 border shadow-sm hover:shadow-md ${
                    isDarkMode
                      ? "bg-gradient-to-r from-indigo-800 to-indigo-900 hover:from-indigo-700 hover:to-indigo-800 text-indigo-300 border-indigo-700 hover:border-indigo-600"
                      : "bg-gradient-to-r from-indigo-50 to-indigo-100 hover:from-indigo-100 hover:to-indigo-200 text-indigo-700 border-indigo-200 hover:border-indigo-300"
                  }`}
                >
                  <HiOutlineViewList className="text-lg" />
                  <span className="text-xs font-medium">Jump to</span>
                  {isDropdownOpen ? (
                    <IoChevronUp className="text-sm" />
                  ) : (
                    <IoChevronDown className="text-sm" />
                  )}
                </button>

                {/* Dropdown Menu */}
                {isDropdownOpen && (
                  <div
                    className={`absolute top-full left-0 mt-2 w-96 rounded-xl shadow-xl border z-50 overflow-hidden ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-600"
                        : "bg-white border-gray-200"
                    }`}
                  >
                    {/* Search Header */}
                    <div
                      className={`p-3 border-b ${
                        isDarkMode
                          ? "border-gray-600 bg-gray-700"
                          : "border-gray-100 bg-gray-50"
                      }`}
                    >
                      <div className="relative">
                        <IoSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
                        <input
                          type="text"
                          placeholder="Search questions..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className={`w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                            isDarkMode
                              ? "bg-gray-600 border-gray-500 text-gray-100 placeholder-gray-400"
                              : "bg-white border-gray-200 text-gray-900 placeholder-gray-500"
                          }`}
                        />
                      </div>
                    </div>

                    {/* Questions List */}
                    <div className="max-h-80 overflow-y-auto">
                      {loading ? (
                        <div
                          className={`px-4 py-8 text-center ${
                            isDarkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mx-auto mb-2"></div>
                          <p className="text-sm">Loading questions...</p>
                        </div>
                      ) : filteredQuestions.length > 0 ? (
                        filteredQuestions.map((question) => (
                          <button
                            key={question.id}
                            onClick={() => handleJumpToQuestion(question.id)}
                            className={`w-full text-left px-4 py-3 transition-colors border-b last:border-b-0 ${
                              question.id === parseInt(id)
                                ? isDarkMode
                                  ? "bg-indigo-900 border-indigo-800"
                                  : "bg-indigo-50 border-indigo-100"
                                : isDarkMode
                                ? "hover:bg-gray-700 border-gray-600"
                                : "hover:bg-gray-50 border-gray-50"
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <div
                                className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                                  question.id === parseInt(id)
                                    ? "bg-indigo-600 text-white"
                                    : question.isSolved
                                    ? "bg-green-100 text-green-700"
                                    : "bg-gray-100 text-gray-600"
                                }`}
                              >
                                {question.isSolved ? "✓" : question.id}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span
                                    className={`text-sm font-medium ${
                                      isDarkMode
                                        ? "text-gray-100"
                                        : "text-gray-900"
                                    }`}
                                  >
                                    Question {question.id}
                                  </span>
                                  <span
                                    className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                                      question.difficulty === "Easy"
                                        ? "bg-green-100 text-green-700"
                                        : question.difficulty === "Medium"
                                        ? "bg-yellow-100 text-yellow-700"
                                        : "bg-red-100 text-red-700"
                                    }`}
                                  >
                                    {question.difficulty}
                                  </span>
                                </div>
                                <p
                                  className={`text-xs line-clamp-2 ${
                                    isDarkMode
                                      ? "text-gray-400"
                                      : "text-gray-600"
                                  }`}
                                >
                                  {question.question &&
                                  question.question.length > 100
                                    ? `${question.question.substring(
                                        0,
                                        100
                                      )}...`
                                    : question.question ||
                                      "Loading question..."}
                                </p>
                              </div>
                            </div>
                          </button>
                        ))
                      ) : questions.length === 0 ? (
                        <div
                          className={`px-4 py-8 text-center ${
                            isDarkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          <p className="text-sm">Failed to load questions</p>
                          <p className="text-xs mt-1">
                            Please refresh the page
                          </p>
                        </div>
                      ) : (
                        <div
                          className={`px-4 py-8 text-center ${
                            isDarkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          <p className="text-sm">No questions found</p>
                          <p className="text-xs mt-1">
                            Try a different search term
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Next Button */}
              <button
                onClick={handleNextPage}
                disabled={isLastQuestion}
                className={`group flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all duration-300 border ${
                  isLastQuestion
                    ? isDarkMode
                      ? "bg-gray-700 text-gray-500 border-gray-600 cursor-not-allowed"
                      : "bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed"
                    : isDarkMode
                    ? "bg-gradient-to-r from-emerald-800 to-emerald-900 hover:from-emerald-700 hover:to-emerald-800 text-emerald-300 border-emerald-700 hover:border-emerald-600 shadow-sm hover:shadow-md"
                    : "bg-gradient-to-r from-emerald-50 to-emerald-100 hover:from-emerald-100 hover:to-emerald-200 text-emerald-700 border-emerald-200 hover:border-emerald-300 shadow-sm hover:shadow-md"
                }`}
                aria-label="Next question"
              >
                <span className="text-xs font-medium">Next</span>
                <FcNext
                  className={`text-lg transition-transform ${
                    !isLastQuestion && "group-hover:translate-x-0.5"
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Mobile Navigation - Simplified */}
          <div className="flex sm:hidden items-center gap-2 ml-2">
            <span className="text-xs font-medium text-gray-600">
              {id}/{totalQuestions > 0 ? totalQuestions : "..."}
            </span>
            <div className="flex gap-1">
              <button
                onClick={handlePrevPage}
                disabled={isFirstQuestion}
                className={`p-1.5 rounded ${
                  isFirstQuestion ? "text-gray-400" : "text-blue-600"
                }`}
              >
                <FcPrevious className="text-lg" />
              </button>
              <button
                onClick={handleNextPage}
                disabled={isLastQuestion}
                className={`p-1.5 rounded ${
                  isLastQuestion ? "text-gray-400" : "text-emerald-600"
                }`}
              >
                <FcNext className="text-lg" />
              </button>
            </div>
          </div>
        </div>

        {/* Right side: User Menu */}
        <div className="flex items-center gap-3">
          {/* Dark Mode Toggle - Only show on subscribers page */}
          {showDarkModeToggle && (
            <button
              onClick={() => setIsDarkMode(!isDarkMode)}
              className={`p-2 rounded-lg transition-all duration-300 ${
                isDarkMode
                  ? "bg-yellow-500 text-white hover:bg-yellow-600"
                  : "bg-gray-700 text-white hover:bg-gray-800"
              } shadow-sm hover:shadow-md`}
              title={
                isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"
              }
            >
              {isDarkMode ? (
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                  />
                </svg>
              ) : (
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                  />
                </svg>
              )}
            </button>
          )}

          <Menu as="div" className="relative">
            <MenuButton
              className={`flex items-center gap-2 p-2 rounded-full transition-colors ${
                isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
              }`}
            >
              <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center">
                <span className="text-white text-sm font-medium">
                  {localStorage.getItem("firstName")?.charAt(0) || "U"}
                </span>
              </div>
              <svg
                className={`w-4 h-4 ${
                  isDarkMode ? "text-gray-300" : "text-gray-600"
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </MenuButton>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <MenuItems
                className={`absolute right-0 mt-2 w-48 origin-top-right rounded-md ${
                  isDarkMode ? "bg-gray-700" : "bg-white"
                } py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none`}
              >
                <div
                  className={`px-4 py-2 border-b ${
                    isDarkMode ? "border-gray-600" : "border-gray-100"
                  }`}
                >
                  <p
                    className={`text-sm font-medium ${
                      isDarkMode ? "text-gray-100" : "text-gray-900"
                    }`}
                  >
                    {localStorage.getItem("firstName") || "User"}
                  </p>
                  <p
                    className={`text-xs ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    {localStorage.getItem("username") || "user@example.com"}
                  </p>
                </div>

                <MenuItem>
                  {({ active }) => (
                    <Link
                      to="/profile"
                      className={`${
                        active
                          ? isDarkMode
                            ? "bg-gray-600"
                            : "bg-gray-100"
                          : ""
                      } flex items-center px-4 py-2 text-sm ${
                        isDarkMode ? "text-gray-200" : "text-gray-700"
                      }`}
                    >
                      <svg
                        className={`mr-3 h-5 w-5 ${
                          isDarkMode ? "text-gray-400" : "text-gray-400"
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                      Profile
                    </Link>
                  )}
                </MenuItem>

                <MenuItem>
                  {({ active }) => (
                    <Link
                      to="/settings"
                      className={`${
                        active
                          ? isDarkMode
                            ? "bg-gray-600"
                            : "bg-gray-100"
                          : ""
                      } flex items-center px-4 py-2 text-sm ${
                        isDarkMode ? "text-gray-200" : "text-gray-700"
                      }`}
                    >
                      <svg
                        className={`mr-3 h-5 w-5 ${
                          isDarkMode ? "text-gray-400" : "text-gray-400"
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      Settings
                    </Link>
                  )}
                </MenuItem>

                <div
                  className={`border-t ${
                    isDarkMode ? "border-gray-600" : "border-gray-100"
                  }`}
                >
                  <MenuItem>
                    {({ active }) => (
                      <button
                        onClick={handleLogout}
                        className={`${
                          active
                            ? isDarkMode
                              ? "bg-gray-600"
                              : "bg-gray-100"
                            : ""
                        } flex w-full items-center px-4 py-2 text-sm text-red-700`}
                      >
                        <svg
                          className="mr-3 h-5 w-5 text-red-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                          />
                        </svg>
                        Sign out
                      </button>
                    )}
                  </MenuItem>
                </div>
              </MenuItems>
            </Transition>
          </Menu>
        </div>
      </div>

      {/* Overlay to close dropdown when clicking outside */}
      {isDropdownOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsDropdownOpen(false)}
        />
      )}
    </nav>
  );
}

export default Topbar;
